import React, {useEffect, useState} from 'react';
import {useSelector} from "react-redux";
import {Button, Col, Container, Row} from 'react-bootstrap';
import {
    ConfirmationDialog
} from "../../Common/ConfirmationDialog";
import {
    GenericModelViewer
} from "../../Common/GenericModelViewer";
import {
    Glyphicon
} from "../../Common/GlyphIcon";
import {
    ObjectUtils
} from "../../../Utils/ObjectUtils";
import {
    Program
} from "../../../Models/StreamBank/Program";
import {
    StreamBankContext
} from "../../../App/StreamBankContext";
import {
    StreamBankLink
} from "../../Common/StreamBankLink";

import {service as ContextService} from "../../../Services/ContextService";
import {service as ProgramService} from "../../../Services/ProgramService";
import {service as ProjectService} from "../../../Services/ProjectService";
import {service as ContextNodeService} from "../../../Services/ContextNodeService";

import {
    useChangeContext,
	useChangeContextAndTool,
} from "../../../Hooks/RouteHooks";
import {
	useShowPermissionDenied
} from "../../../Hooks/ErrorHooks";
import {
	useEditContextNodeId,
	usePermission,
} from "../../../Hooks/PermissionHooks";
import {useMakePageRequest} from "../../../Hooks/RequestHooks";

import {useContextBuilder} from "../../../Hooks/ContextBuilderHooks";
import {useClassNames} from "../../../Hooks/StyleHooks";

import {ChildrenList} from "../../Common/ChildrenList";
import {ContextNodeBenefitSummarySection} from "../../Common/ContextNodeBenefitSummarySection";

import {InfoIcon} from "../../Common/InfoIcon";
import {SbMap} from "../../Common/SbMap";


let model = new Program();

export const ProgramsViewPage = props => {
    const context = useSelector(state => state.contextState.context);
    const currentUser = useSelector(state => state.userState.user);

    const classNames = useClassNames('sb-programs-view-page');
    const contextBuilder = useContextBuilder(props);
    const permission = usePermission();
    const showPermissionDenied = useShowPermissionDenied();
    const editContextNodeId = useEditContextNodeId();
    const changeContext = useChangeContext();
    const changeContextAndTool = useChangeContextAndTool();
    const makePageRequest = useMakePageRequest();

    const [program, setProgram] = useState(null);
    const [childrenPrograms, setChildrenPrograms] = useState([]);
    const [childrenProjects, setChildrenProjects] = useState([]);
    const [existingProgram, setExistingProgram] = useState(null);
    const [programValidation, setProgramValidation] = useState([]);
    const [editMode, setEditMode] = useState(false);
    const [deleteDialog, setDeleteDialog] = useState(false);

    const contextNodeId = program?.contextNodeId;
    const programId = context.childProgram || context.parentProgram;

    useEffect(() => {
        if (programId) {
            loadData();
        }
    }, [programId]);
    
    const setEditModeWithParentProgram = (parentProgram) => {
        setEditMode(true);
        setProgram({
            parentAssignedSpeciesListId: parentProgram?.assignedSpeciesListId,
            organizationId: parentProgram?.organizationId ?? currentUser?.organizationId,
            parentProgramId: parentProgram?.programId ?? null
        });
        setChildrenPrograms([]);
        setChildrenProjects([]);
    }

    const loadData = () => {
        if(programId === "new") {
            if (!permission('create_program')) {
                showPermissionDenied();
            } else {
                let promises = [];
                if (context.parentProgram !== null && context.parentProgram !== "new") {
                    promises.push(
                        ContextNodeService.Lookup({
                                programId: context.parentProgram
                            }
                        ));
                }

                makePageRequest(
                    () => Promise.all(promises),
                    ([contextNodeLookup]) => {
                        setEditModeWithParentProgram(contextNodeLookup?.program);
                    }
                );
            }
        } else {
            makePageRequest(
                () => Promise.all([
                    ContextService.GetContext(programId),
                    ProgramService.GetChildrenPrograms(programId),
                    ProjectService.GetChildrenProjects(programId),
                ])
            ).then(
                ([program, children, projects]) => {
                    setProgram(program.result);
                    setEditMode(false);
                    setChildrenPrograms(children.result);
                    setChildrenProjects(projects.result);
                },
            );
        }
    };

    const canEdit = () => {
        if(program) {
            if(permission('update_program')) {
                return editContextNodeId(program.contextNodeId);
            }
        }
        return false;
    };

    const canDelete = () => {
        if(program) {
            if(permission('delete_program')) {
                return editContextNodeId(program.contextNodeId);
            }
        }

        return false;
    };

    const enterEditMode = () => {
        setEditMode(true);
        setExistingProgram(ObjectUtils.Clone(program));
    };

    const exitEditMode = () => {
        if(existingProgram) {
            setEditMode(false);
            setProgram(ObjectUtils.Clone(existingProgram));
            setExistingProgram(null);
        } else {
            if(context.childProgram === 'new') {
                changeContext({childProgram: null});
            } else {
                changeContext({parentProgram: null});
            }
        }
    };

    const updateProgram = (program) => {
        setProgram(program);
    };

    const saveProgram = () => {
        if(program.programId) {
            makePageRequest(
                _ => ProgramService.UpdateProgram(program),
                x => {
                    setEditMode(false);
                    setProgram(x.result);
                    validateSlug(x.result);

                    // TODO: update breadcrumbs from redux store
                    // instead of rebuilding context
                    contextBuilder();
                },
            );
        } else {
            makePageRequest(
                _ => ProgramService.CreateProgram(program),
                x => {
                    if(x.result.parentProgramId) {
                        changeContextAndTool(
                            {
                                parentProgram: props.match.params.pProgId,
                                childProgram: x.result.slug,
                                project: null,
                                contact: null
                            },
                            "summary"
                        );
                    } else {
                        changeContextAndTool(
                            {
                                parentProgram: x.result.slug,
                                childProgram: null,
                                project: null,
                                contact: null
                            },
                            "summary"
                        );
                    }
                },
            );
        }
    };

    const deleteProgram = () => {
        makePageRequest(
            () => ProgramService.DeleteProgram(program.programId),
            (x) => {
                if(context.childProgram !== null) {
                    changeContext({childProgram: null});
                } else {
                    changeContext({parentProgram: null});
                }

                setDeleteDialog(false);
            },
        );
    };

    const updateValidation = (validation, editorGuid) => {
        //we have multiple model editors on the same object. So let's merge validation keeping any invalid values.
        let target = ObjectUtils.Clone(programValidation);

        //anything that's now invalid should overwrite a property that was previous valid.
        for(let propertyName in target) {
            if(validation[propertyName]) {
                if(target[propertyName].valid && !validation[propertyName].valid) {
                    target[propertyName] = ObjectUtils.Clone(validation[propertyName]);
                }
            }
        }
        //for anything we don't currently have values about. let's add them.
        for(let propertyName in validation) {
            if(!target[propertyName]) {
                target[propertyName] = ObjectUtils.Clone(validation[propertyName]);
            }
        }

        //noneed to clone. we're working with a fresh copy.
        setProgramValidation(target);
    };

    const isValid = () => {
        for(let propertyName in programValidation) {
            let validation = programValidation[propertyName];

            if(!validation.valid) {
                return false;
            }
        }

        return true;
    };

    const getObjTitle = () => {
        if(program && program.parentProgramId) {
            return "Sub-Program";
        }

        return "Program";
    };

    const getContextForProject = (proj) => {
        let newContext = new StreamBankContext(
            (program.parentSlug || program.parentProgramId),
            (program.slug || program.programId),
            (proj.slug || proj.projectId)
        );
        if(!program.parentProgramId) {
            newContext = new StreamBankContext(
                (program.slug || program.programId),
                (proj.parentSlug || proj.programId),
                (proj.slug || proj.projectId)
            );
        }

        return newContext;
    };

    const getMarkers = () => {
        let ret = [];

        if(childrenProjects) {
            childrenProjects.forEach((x) => {
                if(x.Location && x.Location.latitude && x.Location.longitude) {
                    ret.push({
                        key: x.projectId,
                        lat: x.Location.latitude,
                        lng: x.Location.longitude,
                        accuracy: x.Location.accuracy,
                        marker: !permission('authenticated_user') ? 'circle' : 'both',
                        title: x.shortTitle ?? x.title,
                        content: <div>
                            <StreamBankLink Context={getContextForProject(x)}>
                                {x.name}
                            </StreamBankLink>
                            <br />
                            {x.description}
                        </div>
                    });
                }
            });
        }

        return ret;
    };

    const validateSlug = (program) => {
        const { match } = props;

        if(program && match && changeContextAndTool) {
            //if the slug doesn't match, we should update the url.
            if(program.parentProgramId) {
                if(
                    match.params.pProgId !== program.parentSlug ||
                    match.params.cProgId !== program.slug
                ) {
                    changeContextAndTool(
                        {
                            parentProgram: program.parentSlug,
                            childProgram: program.slug,
                            project: null,
                            contact: null
                        },
                        match.params.tool
                    );
                }
            } else {
                if(match.params.pProgID !== program.slug) {
                    changeContextAndTool(
                        {
                            parentProgram: program.slug,
                            childProgram: null,
                            project: null,
                            contact: null
                        },
                        match.params.tool
                    );
                }
            }
        }
    };


    // don't render page before it is loaded
    if(program === null) {
        return null;
    }

    return <Container className={classNames}>
        <Row>
            <ConfirmationDialog
                Title="Confirmation"
                Body={"Are you sure you want to delete this " + getObjTitle() + "?"}
                Display={deleteDialog}
                OnClose={() => {
                    setDeleteDialog(false);
                }}
                OnConfirm={() => {
                    deleteProgram();
                }}
            />
            <Col xs={12} md={6}>
                <h2 className="atomic-sectionheader">
                    <div className="float-right btn-edit">
                        {
                            editMode ? [
                                    <Button
                                        variant="save"
                                        key={1}
                                        onClick={() => saveProgram()}
                                        disabled={!isValid()}
                                    >
                                        Save
                                    </Button>,
                                    <Button
                                        variant="cancel"
                                        key={2}
                                        onClick={() => exitEditMode()}
                                    >
                                        Cancel
                                    </Button>,
                                    programId !== 'new' && canDelete() ?
                                        <Button
                                            variant="delete"
                                            key={3}
                                            onClick={() => setDeleteDialog(true)}
                                        >
                                            Delete
                                        </Button> :
                                        null
                                ] :
                                (
                                    canEdit() ?
                                        <Button
                                            variant="link"
                                            onClick={() => enterEditMode()}>
                                            <Glyphicon glyph="pencil" />Edit
                                        </Button>:
                                        null
                                )
                        }
                    </div>
                    {program && program.name ? program.name: getObjTitle()}
                    <InfoIcon link={program.infoLink}>{program.infoTitle}</InfoIcon>
                </h2>

                {
                    !editMode && program ? [
                            <h3 key="1">Program Goals</h3>,
                            <div className="text-summary pndlm-line-break" key="2">{program.goalsDescription}</div>,
                            <h3 key="3">Program Area</h3>,
                            <div className="text-summary pndlm-line-break" key="4">{program.areaDescription}</div>
                        ] :
                        null
                }

                {
                    program ?
                        <GenericModelViewer
                            IsNewModel={programId === "new"}
                            Model={model}
                            EditMode={editMode}
                            Data={program}
                            Update={updateProgram}
                            OnValidationUpdate={updateValidation}
                            HideGroups={[]}
                            CustomData={{
                                organizationId: program?.organizationId ?? currentUser?.organizationId 
                            }}
                        />:
                        null
                }
                <ContextNodeBenefitSummarySection contextNodeId={contextNodeId} editMode={editMode} />
            </Col>

            <Col xs={12} md={{span: 5, offset:1}}>
                {
                    program && programId !== 'new' && canEdit() ?
                        <Button variant="link" className="float-right h3" onClick={() => {
                            if(program.parentProgramId) {
                                changeContext({
                                    project: "new"
                                });
                            } else {
                                changeContext({
                                    childProgram: "new"
                                });
                            }
                        }
                        }>
                            <Glyphicon glyph="plus-sign" />Add {program.parentProgramId ? "Project" : "Sub-Program"}
                        </Button>:
                        null
                }

                <ChildrenList
                    Title="Sub-Programs"
                    Children={childrenPrograms}
                    Hidden={!program || program.parentProgramId !== null}
                    Sort={(a,b) => {
                        let aEditable = editContextNodeId(a.contextNodeId);
                        let bEditable = editContextNodeId(b.contextNodeId);

                        if (aEditable && !bEditable) {
                            return -1;
                        } else if (!aEditable && bEditable) {
                            return 1;
                        } else {
                            return a.name.localeCompare(b.name);
                        }
                    }
                    }
                    Render={(x) => {
                        let editable = editContextNodeId(x.contextNodeId);

                        return <ul className=" unordered-list">
                            <li className={"unordered" + (editable ? " editable" : null)}>
                                <StreamBankLink
                                    className="link-primary"
                                    Context={
                                    new StreamBankContext(
                                        (program.slug || program.programId),
                                        (x.slug || x.programId)
                                    )
                                }>{x.name}</StreamBankLink>
                            </li>
                        </ul>;
                    }
                    }
                />

                <ChildrenList
                    Title="Projects"
                    Children={childrenProjects}
                    Hidden={!program || program.parentProgramId === null}
                    Sort={(a,b) => {
                        let aEditable = editContextNodeId(a.contextNodeId);
                        let bEditable = editContextNodeId(b.contextNodeId);

                        if (aEditable && !bEditable) {
                            return -1;
                        } else if (!aEditable && bEditable) {
                            return 1;
                        } else {
                            return a.name.localeCompare(b.name);
                        }
                    }
                    }

                    Render={(x) => {
                        let editable = editContextNodeId(x.contextNodeId);

                        return <ul className=" unordered-list">
                            <li className={"unordered" + (editable ? " editable" : null)}>
                                <StreamBankLink className="link-primary" Context={getContextForProject(x)}>{x.name}</StreamBankLink>
                            </li>
                        </ul>;
                    }
                    }
                />

                <ChildrenList
                    Title="Quick Links"
                    Children={program.ContextNodeQuickLinks}
                    Hidden={!program.ContextNodeQuickLinks || !program.ContextNodeQuickLinks.length}
                    Sort={(a,b) => {
                        if (a.order < b.order) {
                            return -1;
                        } else if (a.order > b.order) {
                            return 1;
                        }

                        if (a.title < b.title) {
                            return -1;
                        } else if (a.title > b.title) {
                            return 1;
                        }

                        return 0;
                    }}
                    Render={(x) => {
                        return <ul className=" unordered-list">
                            <li className="unordered">
                                <a className="link-primary" href={x.url} target="_blank">{x.title}</a>
                            </li>
                        </ul>;
                    }
                    }
                />

                {(() => {
                    const markers = getMarkers();

                    if(markers.length) {
                        return <SbMap
                            Lat={markers[0].lat}
                            Lng={markers[0].lng}
                            Zoom={11}
                            Markers={markers}
                        />;
                    }

                    return null;
                })()}
            </Col>
        </Row>
    </Container>;
};


